import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  TextField,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  useTheme,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Assignment as TaskIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  AccessTime as TimeIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Sort as SortIcon,
  FilterList as FilterIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Lightbulb as LightbulbIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';

// Simulation de l'IA pour la priorisation et les suggestions
const aiAnalyzeTask = (task) => {
  const keywords = {
    urgent: ['urgent', 'immédiat', 'critique', 'deadline'],
    important: ['important', 'crucial', 'essentiel', 'clé'],
    legal: ['contrat', 'juridique', 'légal', 'document'],
    financial: ['paiement', 'facture', 'finance', 'comptabilité'],
  };

  let priority = 0;
  let category = 'other';

  // Analyse du titre et de la description
  const text = `${task.title} ${task.description}`.toLowerCase();
  
  for (const [cat, words] of Object.entries(keywords)) {
    if (words.some(word => text.includes(word))) {
      priority += 25;
      category = cat;
    }
  }

  // Analyse de la date d'échéance
  if (task.dueDate) {
    const daysUntilDue = Math.ceil((new Date(task.dueDate) - new Date()) / (1000 * 60 * 60 * 24));
    if (daysUntilDue <= 1) priority += 40;
    else if (daysUntilDue <= 3) priority += 30;
    else if (daysUntilDue <= 7) priority += 20;
  }

  return {
    ...task,
    aiPriority: Math.min(100, priority),
    category,
  };
};

const generateSuggestions = (tasks) => {
  const suggestions = [
    "Regrouper les tâches juridiques similaires",
    "Prioriser les documents avec échéance proche",
    "Planifier une révision des contrats en cours",
    "Mettre à jour les informations administratives",
  ];

  const deadlines = tasks
    .filter(task => task.dueDate && !task.completed)
    .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));

  if (deadlines.length > 0) {
    suggestions.push(`Attention: "${deadlines[0].title}" arrive à échéance bientôt`);
  }

  return suggestions;
};

const SmartTasks = () => {
  const theme = useTheme();
  const [tasks, setTasks] = useState([
    {
      id: 1,
      title: "Révision contrat client urgent",
      description: "Examiner et valider le contrat de service pour le client XYZ",
      dueDate: "2024-03-25",
      completed: false,
      starred: true,
    },
    {
      id: 2,
      title: "Mise à jour documents administratifs",
      description: "Mettre à jour les documents administratifs pour conformité",
      dueDate: "2024-03-28",
      completed: false,
      starred: false,
    },
  ]);

  const [analyzedTasks, setAnalyzedTasks] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [newTask, setNewTask] = useState({ title: '', description: '', dueDate: '' });
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [sortAnchorEl, setSortAnchorEl] = useState(null);
  const [currentFilter, setCurrentFilter] = useState('all');

  useEffect(() => {
    const analyzed = tasks.map(aiAnalyzeTask);
    setAnalyzedTasks(analyzed);
    setSuggestions(generateSuggestions(analyzed));
  }, [tasks]);

  const handleAddTask = () => {
    if (newTask.title.trim()) {
      const task = {
        id: Date.now(),
        ...newTask,
        completed: false,
        starred: false,
      };
      setTasks([...tasks, task]);
      setNewTask({ title: '', description: '', dueDate: '' });
      setOpenDialog(false);
    }
  };

  const handleToggleComplete = (id) => {
    setTasks(tasks.map(task =>
      task.id === id ? { ...task, completed: !task.completed } : task
    ));
  };

  const handleToggleStar = (id) => {
    setTasks(tasks.map(task =>
      task.id === id ? { ...task, starred: !task.starred } : task
    ));
  };

  const handleDelete = (id) => {
    setTasks(tasks.filter(task => task.id !== id));
  };

  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleSortClick = (event) => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleSortClose = () => {
    setSortAnchorEl(null);
  };

  const getFilteredTasks = () => {
    let filtered = [...analyzedTasks];
    
    switch (currentFilter) {
      case 'completed':
        filtered = filtered.filter(task => task.completed);
        break;
      case 'active':
        filtered = filtered.filter(task => !task.completed);
        break;
      case 'starred':
        filtered = filtered.filter(task => task.starred);
        break;
      default:
        break;
    }

    return filtered.sort((a, b) => {
      if (a.starred && !b.starred) return -1;
      if (!a.starred && b.starred) return 1;
      return b.aiPriority - a.aiPriority;
    });
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        height: '100%',
        background: 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(10px)',
        borderRadius: 2,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Tâches Intelligentes
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={handleFilterClick}>
            <FilterIcon />
          </IconButton>
          <IconButton onClick={handleSortClick}>
            <SortIcon />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenDialog(true)}
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            }}
          >
            Nouvelle Tâche
          </Button>
        </Box>
      </Box>

      {suggestions.length > 0 && (
        <Paper
          sx={{
            p: 2,
            mb: 3,
            background: 'rgba(255, 255, 255, 0.5)',
            border: '1px dashed',
            borderColor: theme.palette.primary.main,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <LightbulbIcon color="primary" />
            <Typography variant="subtitle1" color="primary">
              Suggestions de l'IA
            </Typography>
          </Box>
          <List dense>
            {suggestions.map((suggestion, index) => (
              <ListItem key={index}>
                <ListItemIcon>
                  <AutoAwesomeIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={suggestion}
                  primaryTypographyProps={{
                    variant: 'body2',
                    color: 'text.secondary',
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      <List>
        <AnimatePresence>
          {getFilteredTasks().map((task) => (
            <motion.div
              key={task.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              <Paper
                sx={{
                  mb: 2,
                  p: 2,
                  background: task.completed ? 'rgba(0, 0, 0, 0.02)' : 'white',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <IconButton
                    onClick={() => handleToggleComplete(task.id)}
                    sx={{ color: task.completed ? theme.palette.success.main : undefined }}
                  >
                    {task.completed ? <CheckCircleIcon /> : <TaskIcon />}
                  </IconButton>
                  
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          textDecoration: task.completed ? 'line-through' : 'none',
                          color: task.completed ? 'text.secondary' : 'text.primary',
                        }}
                      >
                        {task.title}
                      </Typography>
                      <Chip
                        size="small"
                        label={`Priorité: ${task.aiPriority}%`}
                        sx={{
                          bgcolor: `${theme.palette.primary.main}15`,
                          color: theme.palette.primary.main,
                        }}
                      />
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {task.description}
                    </Typography>
                    
                    {task.dueDate && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <TimeIcon fontSize="small" color="action" />
                        <Typography variant="caption" color="text.secondary">
                          Échéance: {new Date(task.dueDate).toLocaleDateString()}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleToggleStar(task.id)}
                      sx={{ color: task.starred ? theme.palette.warning.main : undefined }}
                    >
                      {task.starred ? <StarIcon /> : <StarBorderIcon />}
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(task.id)}
                      sx={{ color: theme.palette.error.main }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>

                <LinearProgress
                  variant="determinate"
                  value={task.aiPriority}
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: 3,
                    bgcolor: 'rgba(0,0,0,0.05)',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: theme.palette.primary.main,
                    },
                  }}
                />
              </Paper>
            </motion.div>
          ))}
        </AnimatePresence>
      </List>

      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={handleFilterClose}
      >
        <MenuItem onClick={() => { setCurrentFilter('all'); handleFilterClose(); }}>
          Toutes les tâches
        </MenuItem>
        <MenuItem onClick={() => { setCurrentFilter('active'); handleFilterClose(); }}>
          Tâches actives
        </MenuItem>
        <MenuItem onClick={() => { setCurrentFilter('completed'); handleFilterClose(); }}>
          Tâches terminées
        </MenuItem>
        <MenuItem onClick={() => { setCurrentFilter('starred'); handleFilterClose(); }}>
          Tâches importantes
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={sortAnchorEl}
        open={Boolean(sortAnchorEl)}
        onClose={handleSortClose}
      >
        <MenuItem onClick={handleSortClose}>Par priorité</MenuItem>
        <MenuItem onClick={handleSortClose}>Par date d'échéance</MenuItem>
        <MenuItem onClick={handleSortClose}>Par titre</MenuItem>
      </Menu>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nouvelle Tâche</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Titre"
              fullWidth
              value={newTask.title}
              onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={newTask.description}
              onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
            />
            <TextField
              label="Date d'échéance"
              type="date"
              fullWidth
              value={newTask.dueDate}
              onChange={(e) => setNewTask({ ...newTask, dueDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Annuler</Button>
          <Button
            onClick={handleAddTask}
            variant="contained"
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            }}
          >
            Ajouter
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default SmartTasks; 